import { Box, type SxProps } from '@mui/material';
import {
  GridActionsCellItem,
  GridSortDirection,
  type GridCellParams,
  type GridColDef,
  type GridSortItem,
} from '@mui/x-data-grid-pro';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@verantos-dev/core-auth';
import { getCohortsPath } from '@verantos-dev/core-constants';
import { cohortApiSlice } from '@verantos-dev/data-access-cohorts';
import { type DataGridMessages, Icon } from '@verantos-dev/shared-components';
import { theme } from '@verantos-dev/shared-styles';
import { formatDate } from '@verantos-dev/shared-utils';
import { type ProjectDetail } from '@verantos-dev/types-projects';

import { ProjectListActionMenu } from '../containers/ProjectListActionMenu/ProjectListActionMenu';
import { ProjectDialogType, useProjectModalContext } from '../context/modalContext';

const stringCellStyles: SxProps = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  lineHeight: '1.2',
  whiteSpace: 'normal',
};

const clickableCellStyle: SxProps = {
  ...stringCellStyles,
  color: theme.palette.primary.main,
  cursor: 'pointer',
  '&:hover': {
    textDecoration: 'underline',
  },
};

export const useProjectsListPageContent = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const {
    user: { isInternalUser, organizationId: userOrganizationId },
  } = useAuth();

  const messages: DataGridMessages = {
    noRows: intl.formatMessage({ id: 'projectsList.datagrid.noRows' }),
    noResults: intl.formatMessage({ id: 'projectsList.datagrid.noResults' }),
    error: intl.formatMessage({ id: 'projectsList.datagrid.errorMessage' }),
  };

  const sortModel: GridSortItem[] = [
    ...(isInternalUser ? [{ field: 'organizationName', sort: 'asc' as GridSortDirection }] : []),
    { field: 'modifiedOn', sort: 'desc' },
  ];
  const { openDialog } = useProjectModalContext();

  const onStudyCellClick = (params: GridCellParams) => {
    const projectId = params.id.toString();
    const field = params.field;

    if (field === 'projectName') {
      cohortApiSlice.endpoints.getCohorts.initiate({ projectId, urlParams: '' });
      navigate(getCohortsPath(projectId));
    }
  };

  const columnDefinition: GridColDef<ProjectDetail>[] = [
    {
      headerName: intl.formatMessage({
        id: 'projectsList.columnHeader.name',
        defaultMessage: 'Name',
      }),
      type: 'string',
      field: 'projectName',
      flex: 1,
      minWidth: 330,
      renderCell: (params) => {
        const projectName = params?.row.projectName;
        const projectId = params?.row.projectId;
        return (
          <Box data-testid={`project-name-${projectId}`} sx={clickableCellStyle}>
            {projectName}
          </Box>
        );
      },
    },
    {
      headerName: intl.formatMessage({ id: 'projectsList.columnHeader.dataset' }),
      type: 'string',
      field: 'datasetReleaseName',
      flex: 1,
      renderCell: ({ row }) => {
        const { projectId, datasetReleaseName: datasetName, datasetReleaseId: datasetId } = row;

        const handleDatasetClick = () => {
          openDialog(ProjectDialogType.DATASET_DETAILS, null, { datasetId, datasetName });
        };

        return (
          <Box
            data-testid={`project-dataset-${projectId}`}
            sx={clickableCellStyle}
            onClick={handleDatasetClick}
          >
            {datasetName}
          </Box>
        );
      },
      valueGetter: (_, row) => {
        return row.datasetReleaseName || '';
      },
      minWidth: 330,
      sortComparator: (v1, v2) => v1.toString().localeCompare(v2.toString()),
    },
    ...(isInternalUser
      ? ([
          {
            headerName: intl.formatMessage({ id: 'projectsList.columnHeader.organization' }),
            type: 'string',
            field: 'organizationName',
            minWidth: 180,
            flex: 0.5,
            renderCell: ({ row }) => {
              const { projectId, organizationName } = row;
              return (
                <Box data-testid={`project-organization-${projectId}`} sx={stringCellStyles}>
                  {organizationName}
                </Box>
              );
            },
          },
          {
            headerName: intl.formatMessage({ id: 'projectsList.columnHeader.visibility' }),
            type: 'boolean',
            field: 'isVisible',
            minWidth: 180,
            flex: 0.5,
            renderCell: ({ row }) => {
              const { projectId, isVisible, organizationId } = row;
              return (
                <Box data-testid={`project-visibility-${projectId}`} sx={stringCellStyles}>
                  {organizationId !== userOrganizationId
                    ? intl.formatMessage({
                        id: `projectsList.visibility.${isVisible ? 'enabled' : 'disabled'}`,
                      })
                    : '--'}
                </Box>
              );
            },
          },
        ] as GridColDef<ProjectDetail>[])
      : []),
    {
      headerName: intl.formatMessage({ id: 'projectsList.columnHeader.modifiedOn' }),
      type: 'dateTime',
      field: 'modifiedOn',
      minWidth: 230,
      flex: 0.5,
      valueGetter: (_, row) => {
        return new Date(row.modifiedBy.transactionDate);
      },
      renderCell: (params) => {
        const modifiedOn = params.row.modifiedBy.transactionDate
          ? formatDate(params.row.modifiedBy.transactionDate, 'dateTimeFull')
          : '--';
        const projectId = params?.row.projectId;
        return (
          <Box data-testid={`cohort-owner-${projectId}`} sx={stringCellStyles}>
            {modifiedOn}
          </Box>
        );
      },
    },
    {
      field: '',
      headerName: intl.formatMessage({ id: 'common.grid.columnName.actions' }),
      headerAlign: 'right',
      align: 'right',
      flex: 0,
      width: 80,
      type: 'actions',

      getActions: () => {
        return [
          <GridActionsCellItem
            icon={<Icon icon="edit" id="Edit" />}
            label="Edit"
            onClick={() => {
              alert('Edit project');
            }}
          />,
          <GridActionsCellItem
            icon={<Icon icon="delete" id="delete" />}
            label="Delete"
            onClick={() => {
              alert('Delete project');
            }}
          />,
        ];
      },
      renderCell: (param) => {
        return <ProjectListActionMenu project={param.row} />;
      },
      renderHeader: () => null,
    },
  ];

  return {
    sortModel,
    onStudyCellClick,
    columnDefinition,
    messages,
  };
};
